/**
 * Nail Length Selector
 * Step-by-step flow + drag-to-align.
 *
 * Stap 1: foto uploaden
 * Stap 2: gebruiker sleept foto zodat nagel in de outline (align-canvas) valt
 * Stap 3: daarna drie lengtes (small/medium/large) tonen met dezelfde offset
 */

(function () {
  'use strict';

  const TARGET_LENGTHS_MM = {
    small: 11.0,
    medium: 11.5,
    large: 13.0
  };

  const SECTION_SELECTOR = '[data-nail-selector="section"]';

  function initNailLengthSelector() {
    const sections = document.querySelectorAll(SECTION_SELECTOR);
    if (!sections.length) return;
    sections.forEach(setupSection);
  }

  function setupSection(sectionEl) {
    const fileInput = sectionEl.querySelector('[data-nail-selector="file-input"]');
    const statusPlaceholder = sectionEl.querySelector('[data-nail-selector="placeholder"]');
    const loadingEl = sectionEl.querySelector('[data-nail-selector="loading"]');
    const errorEl = sectionEl.querySelector('[data-nail-selector="error"]');
    const previewsEl = sectionEl.querySelector('[data-nail-selector="previews"]');
    const selectionMessageEl = sectionEl.querySelector('[data-nail-selector="selection-message"]');
    const selectButtons = sectionEl.querySelectorAll('[data-nail-selector="select-button"]');
    const step2El = sectionEl.querySelector('[data-nail-selector="step-2"]');
    const step3El = sectionEl.querySelector('[data-nail-selector="step-3"]');
    const step2NextBtn = sectionEl.querySelector('[data-nail-selector="step-2-next"]');
    const alignCanvas = sectionEl.querySelector('[data-nail-selector="align-canvas"]');
    const sizeCanvases = sectionEl.querySelectorAll('canvas[data-nail-selector="canvas"]');

    if (!fileInput || !alignCanvas) return;

    const state = {
      originalImage: null,
      analysis: null,
      overlays: {
        small: null,
        medium: null,
        large: null
      },
      offsetX: 0,
      offsetY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      initialOffsetX: 0,
      initialOffsetY: 0
    };

    function setLoading(isLoading) {
      if (loadingEl) loadingEl.hidden = !isLoading;
      if (statusPlaceholder) statusPlaceholder.hidden = isLoading;
      sectionEl.classList.toggle('nls--loading', isLoading);
    }

    function setError(message) {
      if (!errorEl) return;
      if (message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    }

    function showStep2(show) {
      if (!step2El) return;
      step2El.hidden = !show;
      step2El.classList.toggle('nls-step--disabled', !show);
    }

    function showStep3(show) {
      if (!step3El) return;
      step3El.hidden = !show;
      step3El.classList.toggle('nls-step--disabled', !show);
    }

    function showPreviews(show) {
      if (!previewsEl) return;
      previewsEl.hidden = !show;
    }

    function clearSelectionHighlight() {
      const cards = sectionEl.querySelectorAll('.nls-card');
      cards.forEach((card) => card.classList.remove('nls-card--selected'));
    }

    function setSelectionMessage(text) {
      if (!selectionMessageEl) return;
      if (text) {
        selectionMessageEl.textContent = text;
        selectionMessageEl.hidden = false;
      } else {
        selectionMessageEl.textContent = '';
        selectionMessageEl.hidden = true;
      }
    }

    // ----- STAP 1: Upload -----
    fileInput.addEventListener('change', function () {
      const file = fileInput.files && fileInput.files[0];
      if (!file) return;

      if (!file.type || !file.type.startsWith('image/')) {
        setError('Please upload a valid image file (JPG or PNG).');
        return;
      }

      setError('');
      setSelectionMessage('');
      clearSelectionHighlight();
      showPreviews(false);
      showStep2(false);
      showStep3(false);
      setLoading(true);

      const reader = new FileReader();
      reader.onload = function (event) {
        const img = new Image();
        img.onload = async function () {
          try {
            state.originalImage = img;
            const analysis = await analyzeFingerAndNail(img);
            state.analysis = analysis;
            // Reset drag
            state.offsetX = 0;
            state.offsetY = 0;
            // Toon stap 2 met align-canvas
            drawAlignCanvas();
            showStep2(true);
          } catch (err) {
            console.error('Nail length selector error:', err);
            setError(
              'We could not detect your finger clearly. Please retake the photo with your finger centered and well lit.'
            );
            showStep2(false);
          } finally {
            setLoading(false);
          }
        };
        img.onerror = function () {
          setLoading(false);
          setError('Could not load the image. Please try another file.');
        };
        img.src = event.target.result;
      };
      reader.onerror = function () {
        setLoading(false);
        setError('Error reading the file. Please try again.');
      };

      reader.readAsDataURL(file);
    });

    // ----- STAP 2: Drag (align-canvas) -----
    alignCanvas.style.touchAction = 'none';

    alignCanvas.addEventListener('pointerdown', (e) => {
      if (!state.originalImage) return;
      state.isDragging = true;
      state.dragStartX = e.clientX;
      state.dragStartY = e.clientY;
      state.initialOffsetX = state.offsetX;
      state.initialOffsetY = state.offsetY;
      alignCanvas.setPointerCapture(e.pointerId);
    });

    alignCanvas.addEventListener('pointermove', (e) => {
      if (!state.isDragging) return;
      const dx = e.clientX - state.dragStartX;
      const dy = e.clientY - state.dragStartY;
      state.offsetX = state.initialOffsetX + dx;
      state.offsetY = state.initialOffsetY + dy;
      drawAlignCanvas();
    });

    alignCanvas.addEventListener('pointerup', (e) => {
      if (!state.isDragging) return;
      state.isDragging = false;
      alignCanvas.releasePointerCapture(e.pointerId);
    });

    alignCanvas.addEventListener('pointercancel', (e) => {
      if (!state.isDragging) return;
      state.isDragging = false;
      alignCanvas.releasePointerCapture(e.pointerId);
    });

    // knop: verder naar stap 3
    if (step2NextBtn) {
      step2NextBtn.addEventListener('click', async () => {
        if (!state.originalImage || !state.analysis) return;
        try {
          setLoading(true);
          // overlays genereren (1x, in originele resolutie)
          state.overlays.small = await generateNailOverlayOnly(
            state.originalImage,
            state.analysis,
            TARGET_LENGTHS_MM.small
          );
          state.overlays.medium = await generateNailOverlayOnly(
            state.originalImage,
            state.analysis,
            TARGET_LENGTHS_MM.medium
          );
          state.overlays.large = await generateNailOverlayOnly(
            state.originalImage,
            state.analysis,
            TARGET_LENGTHS_MM.large
          );

          // stap 3 tonen en alle canvassen tekenen met huidige offset
          showStep3(true);
          showPreviews(true);
          redrawSizeCanvases();
        } catch (e) {
          console.error(e);
          setError('Er ging iets mis bij het tonen van de lengtes. Probeer het opnieuw.');
        } finally {
          setLoading(false);
        }
      });
    }

    // ----- STAP 3: Lengtes + selectie -----
    selectButtons.forEach((btn) => {
      btn.addEventListener('click', function () {
        const size = btn.getAttribute('data-nail-size');
        if (!size) return;

        clearSelectionHighlight();
        const card = sectionEl.querySelector('.nls-card[data-nail-size="' + size + '"]');
        if (card) {
          card.classList.add('nls-card--selected');
        }

        const label = btn.parentElement && btn.parentElement.querySelector('.nls-card-label');
        const labelText = label ? label.textContent.trim() : size;
        setSelectionMessage('Je hebt gekozen voor: ' + labelText);
      });
    });

    // ------ helpers voor tekenen ------

    function drawAlignCanvas() {
      if (!state.originalImage || !state.analysis) return;

      const img = state.originalImage;
      const imageWidth = img.naturalWidth || img.width;
      const imageHeight = img.naturalHeight || img.height;

      const maxWidth = 320;
      const maxHeight = 400;
      const scale = Math.min(maxWidth / imageWidth, maxHeight / imageHeight, 1);
      const displayWidth = Math.round(imageWidth * scale);
      const displayHeight = Math.round(imageHeight * scale);

      alignCanvas.width = displayWidth;
      alignCanvas.height = displayHeight;

      const ctx = alignCanvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, displayWidth, displayHeight);

      // Foto met offset
      ctx.drawImage(
        img,
        state.offsetX,
        state.offsetY,
        displayWidth,
        displayHeight
      );

      // Outline (mal) in canvas-coördinaten (vast op scherm)
      drawNailGuide(ctx, displayWidth, displayHeight);
    }

    function redrawSizeCanvases() {
      if (!state.originalImage) return;

      const img = state.originalImage;
      const imageWidth = img.naturalWidth || img.width;
      const imageHeight = img.naturalHeight || img.height;

      sizeCanvases.forEach((canvas) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const maxWidth = 320;
        const maxHeight = 400;
        const scale = Math.min(maxWidth / imageWidth, maxHeight / imageHeight, 1);
        const displayWidth = Math.round(imageWidth * scale);
        const displayHeight = Math.round(imageHeight * scale);

        canvas.width = displayWidth;
        canvas.height = displayHeight;

        ctx.clearRect(0, 0, displayWidth, displayHeight);

        // Foto met dezelfde offset als in stap 2
        ctx.drawImage(
          img,
          state.offsetX,
          state.offsetY,
          displayWidth,
          displayHeight
        );

        const sizeKey = canvas.getAttribute('data-nail-size');
        const overlayCanvas = state.overlays[sizeKey];
        if (overlayCanvas) {
          ctx.drawImage(
            overlayCanvas,
            state.offsetX,
            state.offsetY,
            displayWidth,
            displayHeight
          );
        }

        // Desgewenst kun je hier ook nog de outline tonen,
        // maar dat kan verwarrend zijn; nu alleen de fake nagel.
      });
    }
  }

  // ----- Analyse + overlay in originele resolutie -----

  async function analyzeFingerAndNail(imageElement) {
    const width = imageElement.naturalWidth || imageElement.width;
    const height = imageElement.naturalHeight || imageElement.height;

    if (!width || !height) {
      throw new Error('Invalid image dimensions');
    }

    const fingerWidthRatio = 0.35;
    const fingerHeightRatio = 0.6;
    const fingerTopOffsetRatio = 0.25;

    const fingerWidth = width * fingerWidthRatio;
    const fingerX = (width - fingerWidth) / 2;
    const fingerHeight = height * fingerHeightRatio;
    const fingerY = height * fingerTopOffsetRatio;

    const nailHeightWithinFinger = fingerHeight * 0.3;
    const nailY = fingerY + fingerHeight * 0.1;
    const nailX = fingerX + fingerWidth * 0.15;
    const nailWidth = fingerWidth * 0.7;

    const pxPerMm = null;

    return {
      nailRegion: {
        x: nailX,
        y: nailY,
        width: nailWidth,
        height: nailHeightWithinFinger
      },
      fingerRegion: {
        x: fingerX,
        y: fingerY,
        width: fingerWidth,
        height: fingerHeight
      },
      angle: 0,
      scaleFactors: {
        pxPerMm: pxPerMm
      }
    };
  }

  async function generateNailOverlayOnly(baseImage, analysis, targetLengthMm) {
    const width = baseImage.naturalWidth || baseImage.width;
    const height = baseImage.naturalHeight || baseImage.height;

    if (!width || !height) {
      throw new Error('Invalid base image dimensions');
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    const { nailRegion, scaleFactors } = analysis;

    let lengthMultiplier;
    if (targetLengthMm <= 11.0) {
      lengthMultiplier = 0.5;
    } else if (targetLengthMm <= 11.6) {
      lengthMultiplier = 1.0;
    } else {
      lengthMultiplier = 1.5;
    }

    let extensionPx;
    if (scaleFactors && typeof scaleFactors.pxPerMm === 'number' && scaleFactors.pxPerMm > 0) {
      const relative = targetLengthMm / 11.0;
      extensionPx = nailRegion.height * lengthMultiplier * relative;
    } else {
      extensionPx = nailRegion.height * lengthMultiplier;
    }

    const overlapFactor = 0.4;
    const overlapHeight = nailRegion.height * overlapFactor;
    const extensionHeight = extensionPx;

    const baseX = nailRegion.x;
    const baseY = nailRegion.y + nailRegion.height - overlapHeight;
    const tipWidth = nailRegion.width;
    const totalOverlayHeight = overlapHeight + extensionHeight;

    const averageColor = sampleAverageColor(baseImage, nailRegion);
    const baseColor = `rgba(${averageColor.r}, ${averageColor.g}, ${averageColor.b},`;

    const gradient = ctx.createLinearGradient(
      baseX,
      baseY,
      baseX,
      baseY + totalOverlayHeight
    );
    gradient.addColorStop(0, baseColor + '0.85)');
    gradient.addColorStop(overlapHeight / totalOverlayHeight, baseColor + '0.8)');
    gradient.addColorStop(1, baseColor + '0.6)');

    ctx.save();

    const radius = tipWidth / 2;
    const x = baseX;
    const y = baseY;
    const w = tipWidth;
    const h = totalOverlayHeight;

    roundRectPath(ctx, x, y, w, h, [radius, radius, radius / 3, radius / 3]);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    const highlightGradient = ctx.createLinearGradient(
      x + w / 2,
      y,
      x + w / 2,
      y + h
    );
    highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.35)');
    highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = highlightGradient;
    ctx.fill();

    ctx.lineWidth = Math.max(1, w * 0.02);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
    ctx.stroke();

    ctx.restore();

    const junctionY = baseY;
    const junctionHeight = Math.max(1, overlapHeight * 0.8);
    const junctionGrad = ctx.createLinearGradient(0, junctionY, 0, junctionY + junctionHeight);
    junctionGrad.addColorStop(0, 'rgba(0, 0, 0, 0.08)');
    junctionGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = junctionGrad;
    ctx.fillRect(x, junctionY, w, junctionHeight);

    return canvas;
  }

  function drawNailGuide(ctx, canvasWidth, canvasHeight) {
    const guideW = canvasWidth * 0.35;
    const guideH = canvasHeight * 0.28;
    const guideX = (canvasWidth - guideW) / 2;
    const guideY = canvasHeight * 0.15;
    const guideRadius = guideW / 2;

    ctx.save();
    roundRectPath(
      ctx,
      guideX,
      guideY,
      guideW,
      guideH,
      [guideRadius, guideRadius, guideRadius / 3, guideRadius / 3]
    );
    ctx.closePath();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = Math.max(1.5, guideW * 0.03);
    ctx.setLineDash([6, 5]);
    ctx.stroke();
    ctx.restore();
  }

  function sampleAverageColor(image, region) {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const ctx = tempCanvas.getContext('2d');
    ctx.drawImage(image, 0, 0, width, height);

    const x = Math.max(0, Math.floor(region.x));
    const y = Math.max(0, Math.floor(region.y));
    const w = Math.max(1, Math.floor(region.width));
    const h = Math.max(1, Math.floor(region.height));

    let imageData;
    try {
      imageData = ctx.getImageData(x, y, w, h);
    } catch (e) {
      return { r: 230, g: 200, b: 190 };
    }

    const data = imageData.data;
    let r = 0,
      g = 0,
      b = 0;
    const pixelCount = data.length / 4;
    for (let i = 0; i < data.length; i += 4) {
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
    }
    return {
      r: Math.round(r / pixelCount),
      g: Math.round(g / pixelCount),
      b: Math.round(b / pixelCount)
    };
  }

  function roundRectPath(ctx, x, y, w, h, radii) {
    let tl, tr, br, bl;
    if (typeof radii === 'number') {
      tl = tr = br = bl = radii;
    } else if (Array.isArray(radii)) {
      [tl, tr, br, bl] = radii;
    } else {
      tl = tr = br = bl = 0;
    }

    ctx.beginPath();
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + w - tr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
    ctx.lineTo(x + w, y + h - br);
    ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
    ctx.lineTo(x + bl, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
    ctx.lineTo(x, y + tl);
    ctx.quadraticCurveTo(x, y, x + tl, y);
  }

  window.NailLengthSelector = {
    analyzeFingerAndNail: analyzeFingerAndNail
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNailLengthSelector);
  } else {
    initNailLengthSelector();
  }
})();